/**
 * Shared utility for AG Grid cell editor configuration
 * Handles lookup values, multiline text, and simple text editors
 * 
 * This file provides functions to configure AG Grid cell editors based on column types:
 * - Lookup columns: agRichSelectCellEditor with dropdown values from project structure
 * - Multiline columns: agLargeTextCellEditor with popup editor
 * - Simple columns: agTextCellEditor for inline editing
 */

/**
 * Get lookup values from project structure
 * @param {any} projectStructure
 * @param {string} lookupId
 * @returns {any[]}
 */
export function getLookupValues(projectStructure, lookupId) {
  const lookup = projectStructure?.lookups?.find((l) => l.id === lookupId);
  return lookup?.items || [];
}

/**
 * Get lookup label for a given value ID
 * @param {any} projectStructure
 * @param {string} lookupId
 * @param {string} valueId
 * @returns {string}
 */
export function getLookupLabel(projectStructure, lookupId, valueId) {
  const lookup = projectStructure?.lookups?.find((l) => l.id === lookupId);
  const item = lookup?.items?.find((i) => i.id === valueId);
  return item?.label || valueId;
}

/**
 * Determine cell editor configuration based on column type
 * Returns configuration for agRichSelectCellEditor, agLargeTextCellEditor, or agTextCellEditor
 * @param {any} column
 * @param {any} projectStructure
 * @returns {{ cellEditor?: string; cellEditorPopup?: boolean; cellEditorParams?: any }}
 */
export function getCellEditorConfig(column, projectStructure) {
  // Check if this is a lookup (select) field
  if (column.type === 'lookup' && column.lookupId) {
    const lookupValues = getLookupValues(projectStructure, column.lookupId);
    
    // Create maps for ID <-> label conversion
    const labelToIdMap = new Map(lookupValues.map((item) => [item.label, item.id]));
    const idToLabelMap = new Map(lookupValues.map((item) => [item.id, item.label]));
    
    // Use labels in the dropdown for user-friendly display
    const values = lookupValues.map((item) => item.label);
    
    return {
      cellEditor: 'agRichSelectCellEditor',
      cellEditorPopup: true,
      // Remove cellEditorPopupPosition to use default positioning which aligns with input field
      cellEditorParams: {
        values: values,
        allowTyping: false,
        filterList: true,
        searchType: 'match',
        highlightMatch: true
      }
    };
  }
  
  // Check if this is a multiline field
  if (column.multiline === true) {
    return {
      cellEditor: 'agLargeTextCellEditor',
      cellEditorPopup: true,
      cellEditorParams: {
        maxLength: 5000,
        rows: 10,
        cols: 30
      }
    };
  }
  
  // Default to simple text editor
  return {
    cellEditor: 'agTextCellEditor',
    cellEditorPopup: false,
    cellEditorParams: {
      useFormatter: false
    }
  };
}

/**
 * Get value getter/setter configuration for lookup columns
 * This handles ID to label conversion for editing and label to ID conversion for storage
 * @param {any} column
 * @param {any} projectStructure
 * @returns {{ valueGetter?: (params: any) => any; valueSetter?: (params: any) => boolean } | null}
 */
export function getLookupValueGetterSetter(column, projectStructure) {
  if (column.type !== 'lookup' || !column.lookupId) {
    return null;
  }
  
  const lookupValues = getLookupValues(projectStructure, column.lookupId);
  const idToLabelMap = new Map(lookupValues.map((item) => [item.id, item.label]));
  const labelToIdMap = new Map(lookupValues.map((item) => [item.label, item.id]));
  
  return {
    valueGetter: (params) => {
      // Use colDef.field (which is the header/label) instead of column.id
      const field = params.colDef?.field || column.id;
      const value = params.data[field];
      if (value === '-' || value === undefined || value === null) return '';
      
      // Convert stored ID to label for display in editor
      return idToLabelMap.get(value) || value;
    },
    valueSetter: (params) => {
      // Use colDef.field (which is the header/label) instead of column.id
      const field = params.colDef?.field || column.id;
      let value = params.newValue === '' || params.newValue === null || params.newValue === undefined ? '-' : params.newValue;
      
      // Convert selected label back to ID for storage
      if (value !== '-') {
        value = labelToIdMap.get(value) || value;
      }
      
      params.data[field] = value;
      return true;
    }
  };
}

/**
 * Get cell renderer function that displays lookup labels
 * @param {any} column
 * @param {any} projectStructure
 * @returns {((params: any) => any) | null}
 */
export function getLookupCellRenderer(column, projectStructure) {
  if (column.type !== 'lookup' || !column.lookupId) {
    return null;
  }
  
  return (params) => {
    let displayValue = params.value || '-';
    // For lookup fields, display the label instead of the ID
    if (displayValue !== '-') {
      displayValue = getLookupLabel(projectStructure, column.lookupId, displayValue);
    }
    return displayValue;
  };
}

